<app-banner [title]="'Paiements'" [links]="links"></app-banner>

<div class="container mx-auto px-4 py-6">
	<!-- Search and Actions -->
	<div class="flex items-center justify-between gap-x-7 mb-4">
		<div class="flex items-center gap-x-2 mb-4">
			<label for="search" class="text-[12px]">Rechercher : </label>
			<input type="text" id="search" [value]="searchTerm" (input)="onSearchChange($event)"
				class="placeholder:text-gray-300 text-[12px] rounded-lg border border-gray-300 w-[300px] p-2"
				placeholder="Rechercher par transaction, type ou statut..." />
		</div>
		<button (click)="refreshData()" [disabled]="loading"
			class="bg-primary text-white px-3 py-2 rounded-md flex items-center gap-2 disabled:opacity-50 !text-[12px]">
			<lucide-icon *ngIf="loading" [img]="LoaderIcon" class="animate-spin h-4 w-4"></lucide-icon>
			<lucide-icon *ngIf="!loading" [img]="RefreshCw" class="h-4 w-4"></lucide-icon>
			Actualiser
		</button>
	</div>

	<!-- Error Message -->
	<div *ngIf="error" class="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
		<div class="flex items-center">
			<lucide-icon [img]="AlertCircleIcon" class="h-5 w-5 text-red-400 mr-2"></lucide-icon>
			<span class="text-red-800">{{ error }}</span>
		</div>
	</div>

	<!-- Loading State -->
	<div *ngIf="loading" class="flex justify-center items-center py-12">
		<div class="text-center">
			<lucide-icon [img]="LoaderIcon" class="animate-spin h-8 w-8 text-blue-600 mx-auto mb-4"></lucide-icon>
			<p class="text-gray-600">Chargement des paiements...</p>
		</div>
	</div>

	<!-- Data Table -->
	<div *ngIf="!loading" class="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow mt-7">
		<table class="w-full">
			<!-- head -->
			<thead>
				<tr class="bg-gray-100 border-b border-gray-200">
					<th class="text-left py-3 px-4 text-sm font-semibold text-gray-900">N° Transaction</th>
					<th class="text-left py-3 px-4 text-sm font-semibold text-gray-900">Date</th>
					<th class="text-left py-3 px-4 text-sm font-semibold text-gray-900">Type de paiement</th>
					<th class="text-left py-3 px-4 text-sm font-semibold text-gray-900">Montant</th>
					<th class="text-left py-3 px-4 text-sm font-semibold text-gray-900">Statut</th>
					<th class="text-left py-3 px-4 text-sm font-semibold text-gray-900">Description</th>
					<th class="text-left py-3 px-4 text-sm font-semibold text-gray-900"></th>
				</tr>
			</thead>
			<tbody>
				<tr *ngFor="let payment of paginatedPayments"
					class="hover:bg-gray-50 border-b border-gray-100 cursor-pointer transition-colors"
					(click)="navigateToDetails(payment)">
					<td class="py-3 px-4 text-sm text-gray-900 font-medium">
						{{ payment.transactionId }}
					</td>
					<td class="py-3 px-4 text-sm text-gray-900">
						{{ payment.date }}
					</td>
					<td class="py-3 px-4 text-sm text-gray-900 font-medium">{{ payment.paymentType }}</td>
					<td class="py-3 px-4 text-sm text-gray-900 font-semibold">
						{{ formatAmount(payment.amount, payment.currency) }}
					</td>
					<td class="py-3 px-4">
						<span [class]="getStatusBadgeClass(payment.status)">
							{{ getStatusText(payment.status) }}
						</span>
					</td>
					<td class="py-3 px-4 text-sm text-gray-900 max-w-xs truncate" [title]="payment.description">
						{{ payment.description || '-' }}
					</td>
					<td class="py-3 px-4" (click)="$event.stopPropagation()">
						<button
							*ngIf="canPay(payment)"
							type="button"
							(click)="openPaymentModal(payment, $event)"
							class="btn btn-primary btn-sm !text-xs">
							Payer
						</button>
						<lucide-icon
							*ngIf="!canPay(payment)"
							[img]="ChevronRightIcon"
							class="h-4 w-4 text-gray-400"></lucide-icon>
					</td>
				</tr>
			</tbody>
		</table>
	</div>

	<!-- Pagination -->
	<div *ngIf="!loading && filteredPayments.length > 0"
		class="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6">
		<div class="text-sm text-gray-600 dark:text-gray-400">
			Affichage de {{ (currentPage - 1) * itemsPerPage + 1 }} à {{ Math.min(currentPage * itemsPerPage,
			filteredPayments.length) }}
			sur {{ filteredPayments.length }} résultats
		</div>

		<div class="flex items-center space-x-1">
			<button
				class="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
				[disabled]="currentPage === 1" (click)="changePage(currentPage - 1)">
				<lucide-icon [img]="ChevronLeftIcon" class="h-4 w-4"></lucide-icon>
			</button>

			<button *ngFor="let page of pages" class="px-3 py-2 text-sm font-medium border border-gray-300 hover:bg-gray-50"
				[class]="page === currentPage ? 'bg-primary text-white border-primbg-primary' : 'bg-white text-gray-700'"
				(click)="changePage(page)">
				{{ page }}
			</button>

			<button
				class="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-r-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
				[disabled]="currentPage === totalPages" (click)="changePage(currentPage + 1)">
				<lucide-icon [img]="ChevronRightIcon" class="h-4 w-4"></lucide-icon>
			</button>
		</div>
	</div>

	<!-- Empty state -->
	<div *ngIf="!loading && filteredPayments.length === 0" class="text-center py-12">
		<div class="max-w-md mx-auto">
			<lucide-icon class="mx-auto h-12 w-12 text-gray-400" [img]="FileText"></lucide-icon>
			<h3 class="mt-2 text-sm font-medium text-gray-900">
				{{ searchTerm ? 'Aucun résultat trouvé' : 'Aucun paiement' }}
			</h3>
			<p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
				{{ searchTerm ? 'Aucun paiement ne correspond à votre recherche.' : 'Aucun paiement trouvé pour les
				critères sélectionnés.' }}
			</p>
			<button *ngIf="searchTerm" (click)="searchTerm = ''; currentPage = 1" class="mt-4 btn btn-primary">
				Effacer la recherche
			</button>
		</div>
	</div>
</div>

